import Component from '@glimmer/component';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{!-- «UI nuova» empty state (NvEmptyState, spec docs/ui-nuova/components/lists.md §6). Styles: app/styles/nuova/_empty-state.scss. --}}\n{{#if @inline}}\n  <div class=\'nv-empty-line\' data-ui-part=\'empty-state\' ...attributes>\n    {{#if this.lineText}}<span class=\'nv-empty-line-text\'>{{this.lineText}}</span>{{/if}}\n    {{#if @help}}<span class=\'nv-empty-line-help\'>{{@help}}</span>{{/if}}\n    {{yield to=\'body\'}}\n    {{yield to=\'actions\'}}\n  </div>\n{{else}}\n  <div class=\'nv-empty\' data-ui-part=\'empty-state\' ...attributes>\n    {{#if @icon}}\n      <span class=\'nv-empty-tile\' aria-hidden=\'true\'><FaIcon @icon={{@icon}} /></span>\n    {{/if}}\n    {{#if this.isH3}}\n      <h3 class=\'nv-empty-title\'>{{@title}}</h3>\n    {{else if this.isH4}}\n      <h4 class=\'nv-empty-title\'>{{@title}}</h4>\n    {{else}}\n      <h2 class=\'nv-empty-title\'>{{@title}}</h2>\n    {{/if}}\n    {{#if @text}}\n      <p class=\'nv-empty-text\'>{{@text}}</p>\n    {{/if}}\n    {{yield to=\'body\'}}\n    {{#if (has-block \'actions\')}}\n      <div class=\'nv-empty-actions\'>{{yield to=\'actions\'}}</div>\n    {{/if}}\n  </div>\n{{/if}}\n");

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component — «UI nuova» empty state (spec docs/ui-nuova/components/lists.md §6,
 *              RULES_V2 §3: 48px icon tile + title + one line + primary + optional
 *              secondary; never an empty area). The host keeps its own conditions (when
 *              the list is empty, role gates of the actions) and its buttons in its
 *              template; this component only draws the anatomy.
 *   extends:   Component (@glimmer/component)
 *   template:  nv-empty-state.hbs
 *   args:      @icon     FontAwesome icon name for the 48px tile (omit: no tile)
 *              @title    translated title, 15/600 (required unless @inline)
 *              @text     translated one-line explanation, 13.5 secondary
 *              @inline   true: the in-card variant — ONE 13.5 grey line (@text, or @title
 *                        when no text), no tile, no title; the actions block sits on the
 *                        same line (a link / small button)
 *              @help     @inline only: optional translated help, a second 12px --nv-sec line under
 *                        the grey line (after the actions row: CSS order + full basis), no tile,
 *                        no box; ignored by the block state (its @text already is the help)
 *              @level    heading level of the title, 2 (default) | 3 | 4
 *              ...attributes on the root (data-test hooks, a host layout class only)
 *   blocks:    <:body>    extra lines under the text (e.g. «no permission to add»)
 *              <:actions> the primary button first, then the optional secondary
 *                         (nv-btn markup of the host: keeps its own data-test hooks)
 *   uses:      FaIcon; styles app/styles/nuova/_empty-state.scss (mixins _rows.scss)
 *   siblings:  BbEmptyState (classic SVG illustration, not used in nuova)
 *   gotchas:   (1) class names are the historic .nv-empty* ones on purpose: hand-written
 *              templates and tests (`.nv-empty-title`) keep working while they migrate.
 *              (2) loading is not empty: while the first load runs, draw skeletons
 *              (nv-skel-bar), never this component.
 */
class NvEmptyState extends Component {
  get level() {
    const level = Number(this.args.level);
    return level === 3 || level === 4 ? level : 2;
  }
  get isH3() {
    return this.level === 3;
  }
  get isH4() {
    return this.level === 4;
  }
  get lineText() {
    return this.args.text ?? this.args.title;
  }
}
setComponentTemplate(TEMPLATE, NvEmptyState);

export { NvEmptyState as default };
//# sourceMappingURL=nv-empty-state.js.map
